/** Staff mobile apps must never land on the manager login.
 *  TS = universal staff app (/ts/) — KA (/pp/), DA (/dd/), TI (/ti/) are portions inside TS.
 *  See .cursor/rules/ts-app.mdc and .cursor/rules/staff-portals-ka-da.mdc */

import { MANAGER_TOKEN_KEY } from "@/lib/authSession";
import { appPathname } from "@/lib/opsBase";

/**
 * Portal detection runs on raw `window.location.pathname`, which still carries
 * the `/ops` basename on the Team hub. Strip it here — the single chokepoint —
 * so every caller keeps matching the same patterns on both hosts.
 */
function portalPath(pathname) {
  return appPathname(pathname || "");
}

export const STAFF_PORTAL_KEY = "ttt_staff_portal";
/** @deprecated use STAFF_PORTAL_START_BY_KIND */
export const STAFF_PORTAL_START_KEY = "ttt_staff_portal_start";

export const STAFF_PORTAL_START_BY_KIND = {
  ts: "ttt_staff_portal_ts_start",
  tb: "ttt_staff_portal_ts_start",
  driver: "ttt_staff_portal_driver_start",
  kitchen: "ttt_staff_portal_kitchen_start",
  inventory: "ttt_staff_portal_inventory_start",
  chat: "ttt_staff_portal_chat_start",
  salary: "ttt_staff_portal_salary_start",
  we: "ttt_staff_portal_we_start",
};

/** Customer window (CW) — public cake-proof links; not KA/DA staff portals. */
export const CUSTOMER_WINDOW_PATH_RE = /^\/p\/(?:cake-proof|frosting-proof)\//;

export const TS_PORTAL_PATH_RE = /^\/ts(?:\/|$)/;

export const STAFF_PORTAL_PATH_RE =
  /^\/(ts(?:\/|$)|tb(?:\/|$)|pp\/|dd\/|ti\/|ca\/|sa\/|we\/|ka\/[tu]\/|driver|kitchen|d\/|p\/(?:baker|froster|decorator)\/|production\/(?:baker|froster|decorator)|calculator\/(?:baker|froster))/;

export const CHAT_PORTAL_PATH_RE = /^\/ca\//;
export const SA_PORTAL_PATH_RE = /^\/sa\//;
export const WE_PORTAL_PATH_RE = /^\/we\//;

export const INVENTORY_PORTAL_PATH_RE = /^\/ti\//;

export const DRIVER_PORTAL_PATH_RE = /^\/dd\//;
export const KITCHEN_PORTAL_PATH_RE = /^\/pp\//;

/** Legacy paths still routed into the matching portal (redirect to /pp/hub or /dd/hub). */
export const DRIVER_PORTAL_LEGACY_PATH_RE = /^\/(driver|d\/)/;
export const KITCHEN_PORTAL_LEGACY_PATH_RE =
  /^\/(kitchen|production\/|p\/(?:baker|froster|decorator)\/|calculator\/(?:baker|froster))/;

export function isCustomerWindowPath(pathname) {
  return CUSTOMER_WINDOW_PATH_RE.test(portalPath(pathname));
}

export function portalKindFromPath(pathname) {
  const path = portalPath(pathname);
  if (isCustomerWindowPath(path)) return null;
  if (TS_PORTAL_PATH_RE.test(path)) return "ts";
  if (DRIVER_PORTAL_PATH_RE.test(path) || DRIVER_PORTAL_LEGACY_PATH_RE.test(path)) return "driver";
  if (KITCHEN_PORTAL_PATH_RE.test(path) || KITCHEN_PORTAL_LEGACY_PATH_RE.test(path) || /^\/ka\/[tu]\//.test(path))
    return "kitchen";
  if (INVENTORY_PORTAL_PATH_RE.test(path)) return "inventory";
  if (CHAT_PORTAL_PATH_RE.test(path)) return "chat";
  if (SA_PORTAL_PATH_RE.test(path)) return "salary";
  if (WE_PORTAL_PATH_RE.test(path)) return "we";
  return null;
}

export function isStaffPortalPath(pathname) {
  return STAFF_PORTAL_PATH_RE.test(portalPath(pathname));
}

export function isManagerAuthenticated() {
  try {
    return Boolean(localStorage.getItem(MANAGER_TOKEN_KEY));
  } catch {
    return false;
  }
}

export function touchStaffPortalKind(kind) {
  if (!kind || isManagerAuthenticated()) return;
  try {
    localStorage.setItem(STAFF_PORTAL_KEY, kind);
  } catch {
    /* private mode */
  }
}

export function enterStaffPortal(kind, startUrl) {
  if (!kind || isManagerAuthenticated()) return;
  try {
    localStorage.setItem(STAFF_PORTAL_KEY, kind);
    const startKey = STAFF_PORTAL_START_BY_KIND[kind];
    if (startUrl && startKey) {
      localStorage.setItem(startKey, startUrl);
    }
    if (startUrl) {
      localStorage.setItem(STAFF_PORTAL_START_KEY, startUrl);
    }
  } catch {
    /* private mode */
  }
}

export function getStaffPortalKind() {
  try {
    return localStorage.getItem(STAFF_PORTAL_KEY);
  } catch {
    return null;
  }
}

export function getStaffPortalStart(kind) {
  const portalKind = kind || getStaffPortalKind();
  if (!portalKind) return null;
  try {
    const keyed = STAFF_PORTAL_START_BY_KIND[portalKind];
    if (keyed) {
      const url = localStorage.getItem(keyed);
      if (url) return url;
    }
    return localStorage.getItem(STAFF_PORTAL_START_KEY);
  } catch {
    return null;
  }
}

/** Token from saved kitchen/driver home URL (for QR deep-links after first KA open). */
export function readStaffPortalTokenFromStoredStart(kind = "kitchen") {
  const start = getStaffPortalStart(kind);
  if (!start) return "";
  try {
    const u = new URL(start, window.location.origin);
    const fromQuery = (u.searchParams.get("token") || "").trim();
    if (fromQuery) return fromQuery;
    const hash = u.hash || "";
    if (hash.startsWith("#t=")) return decodeURIComponent(hash.slice(3).trim());
  } catch {
    /* ignore */
  }
  return "";
}

export function shouldRedirectAwayFromManager(pathname) {
  // TM and TS share an origin but not navigation. Remembered staff state must
  // never redirect a manager URL; staff apps open only through explicit paths.
  return false;
}

const MANIFEST_BY_KIND = {
  ts: "/ts/manifest.webmanifest",
  tb: "/ts/manifest.webmanifest",
  driver: "/dd/manifest.webmanifest",
  kitchen: "/pp/manifest.webmanifest",
  inventory: "/ti/manifest.webmanifest",
  chat: "/ca/manifest.webmanifest",
  salary: "/sa/manifest.webmanifest",
  we: "/we/manifest.webmanifest",
};

const STAFF_PORTAL_ICON_VERSION = "4";

const ICON_BY_KIND = {
  ts: `/ts/icon-192.png?v=${STAFF_PORTAL_ICON_VERSION}`,
  tb: `/ts/icon-192.png?v=${STAFF_PORTAL_ICON_VERSION}`,
  driver: `/dd/icon-192.png?v=${STAFF_PORTAL_ICON_VERSION}`,
  kitchen: `/pp/icon-192.png?v=${STAFF_PORTAL_ICON_VERSION}`,
  inventory: `/ti/icon-192.png?v=${STAFF_PORTAL_ICON_VERSION}`,
  chat: `/ts/icon-192.png?v=${STAFF_PORTAL_ICON_VERSION}`,
  salary: `/ts/icon-192.png?v=${STAFF_PORTAL_ICON_VERSION}`,
  we: `/ts/icon-192.png?v=${STAFF_PORTAL_ICON_VERSION}`,
};

const TITLE_BY_KIND = {
  ts: "TS — The Table Top",
  tb: "TS — The Table Top",
  driver: "Delivery — The Table Top",
  kitchen: "Kitchen — The Table Top",
  inventory: "Inventory — The Table Top",
  chat: "Chat Report — The Table Top",
  salary: "Salary & Attendance — The Table Top",
  we: "Work Ethic — The Table Top",
};

const APPLE_TITLE_BY_KIND = {
  ts: "TS",
  driver: "Delivery",
  kitchen: "Kitchen",
  inventory: "Inventory",
  chat: "Chat",
  salary: "SA",
  we: "WE",
};

export function applyStaffPortalDocument(kind) {
  const manifestHref = MANIFEST_BY_KIND[kind];
  if (!manifestHref) return;

  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  if (link.getAttribute("href") !== manifestHref) {
    link.setAttribute("href", manifestHref);
  }

  if (TITLE_BY_KIND[kind]) {
    document.title = TITLE_BY_KIND[kind];
  }

  const appleTitle = APPLE_TITLE_BY_KIND[kind];
  if (appleTitle) {
    let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "apple-mobile-web-app-title";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", appleTitle);
  }

  const iconHref = ICON_BY_KIND[kind];
  if (iconHref) {
    let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (!appleIcon) {
      appleIcon = document.createElement("link");
      appleIcon.rel = "apple-touch-icon";
      document.head.appendChild(appleIcon);
    }
    if (appleIcon.getAttribute("href") !== iconHref) {
      appleIcon.setAttribute("href", iconHref);
    }
  }
}

export function markStaffPortalFromPath(pathname) {
  const kind = portalKindFromPath(pathname);
  if (!kind) return null;
  // Match on the un-prefixed path, but store the caller's own path: the start
  // URL is navigated to later and must stay valid in whatever space it came
  // from (router paths are already basename-relative, raw ones are not).
  const path = portalPath(pathname);
  const start = String(pathname || "");
  if (/^\/(ca|sa|pp|dd|ti)\/hub/.test(path)) {
    enterStaffPortal(kind, start);
  } else if (kind === "ts" && TS_PORTAL_PATH_RE.test(path)) {
    enterStaffPortal("ts", path === "/ts/" ? start.replace(/\/$/, "") : start);
  } else {
    touchStaffPortalKind(kind);
  }
  applyStaffPortalDocument(kind);
  return kind;
}
